/**
 * Admin Panel - Secure Entry Point
 * This is the main admin interface that users access after login
 * Analytics and other admin features are nested within this panel
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, BarChart3, Users, Settings, LogOut, Home } from 'lucide-react';
import { TokenManager } from '../api/config';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api';
import AdminAnalytics from './AdminAnalytics';

interface AdminUser {
  email: string;
  full_name: string;
  is_admin: boolean;
  is_superadmin: boolean;
}

const AdminPanel: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<'analytics' | 'users' | 'settings'>('analytics');
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    loadAdminUser();
  }, []);

  const loadAdminUser = async () => {
    try {
      const user = await authAPI.getCurrentUser();

      // Verify user is actually an admin
      if (!user.is_admin && !user.is_superadmin) {
        navigate('/');
        return;
      }

      setAdminUser(user);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load admin user:', error);
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Title */}
            <div className="flex items-center space-x-3">
              <Shield className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
                <p className="text-xs text-gray-500">Prindox Management System</p>
              </div>
            </div>

            {/* User Info & Actions */}
            <div className="flex items-center space-x-4">
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-gray-900">{adminUser?.full_name}</p>
                <p className="text-xs text-gray-500">
                  {adminUser?.is_superadmin ? 'Superadmin' : 'Admin'}
                </p>
              </div>

              <button
                onClick={() => navigate('/')}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Home"
              >
                <Home className="w-5 h-5" />
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setCurrentTab('analytics')}
              className={`
                flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${currentTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>

            <button
              onClick={() => setCurrentTab('users')}
              className={`
                flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                ${currentTab === 'users'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <Users className="w-4 h-4" />
              <span>Users</span>
            </button>

            {adminUser?.is_superadmin && (
              <button
                onClick={() => setCurrentTab('settings')}
                className={`
                  flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${currentTab === 'settings'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Content Area */}
      <main>
        {currentTab === 'analytics' && <AdminAnalytics />}

        {currentTab === 'users' && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">User Management</h2>
              <p className="text-gray-600">User management features coming soon...</p>
            </div>
          </div>
        )}

        {currentTab === 'settings' && adminUser?.is_superadmin && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">System Settings</h2>
              <p className="text-gray-600">System settings coming soon...</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
